import React, { useState, useEffect } from 'react';
import { useFeedback } from '../../contexts/FeedbackContext';
import TermsAndConditionsModal from '../../components/common/TermsAndConditionsModal';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import campaignService, { isCampaignActive } from '../../services/campaignService';
import savedCampaignService from '../../services/savedCampaignService';
import userService from '../../services/userService';
import campaignParticipantService from '../../services/campaignParticipantService';
import analyticsService from '../../services/analyticsService';
import trackingLinkService from '../../services/trackingLinkService';
import contentMonitoringService from '../../services/contentMonitoringService';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText } from '../../security/sanitize';

// Icons
import { 
  Bookmark, 
  Tag, 
  Users, 
  Banknote, 
  Calendar, 
  Clock, 
  BadgeCheck, 
  X,
  AlertTriangle,
  ArrowLeft,
  Send,
  Share2,
  Store,
  CheckCircle2,
  RefreshCw,
  Sparkles,
  Star,
} from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import CampaignProgressIndicator from '../../components/common/CampaignProgressIndicator';
import ReviewModal from '../../components/common/ReviewModal';
import ReportFlag from '../../components/common/ReportFlag';
import Breadcrumb from '../../components/common/Breadcrumb';

// --- Helpers ---

const formatDate = (timestamp) => {
  if (!timestamp) return 'TBD';
  try {
    const date = timestamp instanceof Date ? timestamp : timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'TBD';
  }
};

const formatPeso = (amount) => {
  const cleaned = (amount || 0).toString().replace(/[₱,\s]/g, '');
  const num = parseInt(cleaned, 10);
  if (isNaN(num)) return '₱0';
  return `₱${num.toLocaleString('en-US')}`;
};

const getCompensationLabel = (campaign) => {
  if (campaign.compensationType === 'monetary') {
    return formatPeso(campaign.compensationAmount || campaign.budget);
  }
  if (campaign.compensationType === 'product') {
    return campaign.productDetails || 'Product';
  }
  return `${formatPeso(campaign.compensationAmount || campaign.budget)} + Product`;
};

const getDaysUntil = (timestamp) => {
  if (!timestamp) return null;
  try {
    const date = timestamp instanceof Date ? timestamp : timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applicationNote, setApplicationNote] = useState('');
  const [reviewSubmissionStatus, setReviewSubmissionStatus] = useState(null);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [proofSubmissionStatus, setProofSubmissionStatus] = useState(null);
  const [proofFeedback, setProofFeedback] = useState('');
  const [activeCampaignCount, setActiveCampaignCount] = useState(0);
  const [otherCampaigns, setOtherCampaigns] = useState([]);
  const [participantData, setParticipantData] = useState(null);
  const [revisionRequested, setRevisionRequested] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  
  // State for draft workflow
  const [draftStatus, setDraftStatus] = useState('not_submitted');
  const [draftFeedback, setDraftFeedback] = useState('');
  const [requiresDraftApproval, setRequiresDraftApproval] = useState(true);
  
  // State for tracking link
  const [trackingLink, setTrackingLink] = useState(null);
  const [trackingLinkLoading, setTrackingLinkLoading] = useState(false);

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAcceptedDate, setTermsAcceptedDate] = useState(null);
  const [pendingApplicationData, setPendingApplicationData] = useState(null);

  // State for brand data
  const [brandData, setBrandData] = useState(null);
  
  // Toast notification system
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();
  
  // Helper function to determine current stage for progress indicator
  const getCurrentStage = () => {
    if (applicationStatus !== 'approved') return 'applied';
    
    if (requiresDraftApproval) {
      // Pre-approval workflow
      if (draftStatus === 'not_submitted') return 'approved';
      if (draftStatus === 'pending' || draftStatus === 'submitted') return 'draft_submitted';
      if (draftStatus === 'rejected' || draftStatus === 'revision_requested') return 'draft_submitted'; // Show as draft submitted but needs revision
      if (draftStatus === 'approved') {
        // Draft approved, check proof status
        if (!proofSubmissionStatus && !reviewSubmissionStatus) return 'draft_approved';
        if (proofSubmissionStatus === 'pending' || reviewSubmissionStatus === 'pending') return 'proof_submitted';
        if (proofSubmissionStatus === 'approved' || reviewSubmissionStatus === 'approved') return 'paid';
        return 'draft_approved'; // Draft approved, ready to post and submit proof
      }
    } else {
      // Legacy workflow (no draft approval required)
      if (!proofSubmissionStatus && !reviewSubmissionStatus) return 'approved';
      if (proofSubmissionStatus === 'pending' || reviewSubmissionStatus === 'pending') return 'proof_submitted';
      if (proofSubmissionStatus === 'approved' || reviewSubmissionStatus === 'approved') return 'paid';
      return 'approved'; // Ready to submit proof
    }
    
    return 'approved';
  };
  
  // Helper function to get current status message
  const getCurrentStatusMessage = () => {
    if (requiresDraftApproval) {
      // Pre-approval workflow messages
      if (draftStatus === 'not_submitted') {
        return (
          <p className="text-sm text-blue-700 font-medium">
            Ready to submit your content draft for brand approval!
          </p>
        );
      }
      if (draftStatus === 'pending') {
        return (
          <p className="text-sm text-yellow-700 font-medium">
            Draft submitted - awaiting brand approval before you can post content.
          </p>
        );
      }
      if (draftStatus === 'rejected') {
        return (
          <p className="text-sm text-orange-700 font-medium">
            Draft needs revision - please update and resubmit your content draft.
          </p>
        );
      }
      if (draftStatus === 'approved') {
        if (!proofSubmissionStatus && !reviewSubmissionStatus) {
          return (
            <p className="text-sm text-green-700 font-medium">
              Draft approved! You can now post your content and submit proof when ready.
            </p>
          );
        }
        if (proofSubmissionStatus === 'pending' || reviewSubmissionStatus === 'pending') {
          return (
            <p className="text-sm text-blue-700 font-medium">
              Proof submitted - awaiting final brand review.
            </p>
          );
        }
        if (proofSubmissionStatus === 'approved' || reviewSubmissionStatus === 'approved') {
          return (
            <p className="text-sm text-green-700 font-medium">
              Content approved! Payment will be processed after campaign ends.
            </p>
          );
        }
        if (proofSubmissionStatus === 'rejected' || reviewSubmissionStatus === 'rejected') {
          return (
            <p className="text-sm text-orange-700 font-medium">
              Revision requested - please resubmit your proof of posting.
            </p>
          );
        }
      }
    } else {
      // Legacy workflow messages
      if (!proofSubmissionStatus && !reviewSubmissionStatus) {
        return (
          <p className="text-sm text-blue-700 font-medium">
            Ready to create content! Submit your proof when ready.
          </p>
        );
      }
      if (proofSubmissionStatus === 'pending' || reviewSubmissionStatus === 'pending') {
        return (
          <p className="text-sm text-blue-700 font-medium">
            Content submitted - awaiting brand review.
          </p>
        );
      }
      if (proofSubmissionStatus === 'approved' || reviewSubmissionStatus === 'approved') {
        return (
          <p className="text-sm text-green-700 font-medium">
            Content approved! Payment will be processed after campaign ends.
          </p>
        );
      }
      if (proofSubmissionStatus === 'rejected' || reviewSubmissionStatus === 'rejected') {
        return (
          <p className="text-sm text-orange-700 font-medium">
            Revision requested - please resubmit your content.
          </p>
        );
      }
    }
    
    return (
      <p className="text-sm text-gray-700 font-medium">
        Campaign in progress...
      </p>
    );
  };
  
  // State for profile completeness check
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [missingFields, setMissingFields] = useState([]);
  
  // Check profile completeness on component mount and when userProfile changes
  useEffect(() => {
    if (userProfile) {
      const profileCheck = userService.isProfileComplete(userProfile);
      setIsProfileComplete(profileCheck.isComplete);
      setMissingFields(profileCheck.missingFields);
    }
  }, [userProfile]);

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch campaign details from Firestore
        const campaignData = await campaignService.getCampaignById(id);

        if (!campaignData) {
          setCampaign(null);
          return;
        }
        
        // Fetch brand details
        const brandUser = await userService.getUserById(campaignData.brandId);

        
        // Fetch active campaign count for this brand
        try {
          const brandCampaigns = await campaignService.getCampaignsByBrandId(campaignData.brandId, { status: 'active' });
          // Filter out campaigns that have ended (past end date) and the current campaign
          const trulyActiveCampaigns = brandCampaigns.filter(c => isCampaignActive(c) && c.id !== id);
          setActiveCampaignCount(trulyActiveCampaigns.length);
          setOtherCampaigns(trulyActiveCampaigns.slice(0, 3));
        } catch (err) {
          console.error('Error fetching active campaign count:', err);
          setActiveCampaignCount(0);
        }
        
        if (brandUser) {
          const brandDetails = {
            id: brandUser.uid || brandUser.id,
            name: brandUser.companyName || brandUser.displayName || 'Brand',
            logo: brandUser.logo || brandUser.profileImageUrl || brandUser.profileImage || '/assets/default-logo.png',
            description: brandUser.description || brandUser.bio || brandUser.companyDescription || 'No description available.',
            website: brandUser.website || '#',
            location: brandUser.location || 'Not specified'
          };


          setBrandData(brandDetails);
          
          // Add brand data to campaign object
          campaignData.brand = brandDetails;
        } else {

          // Set default brand data if brand user not found
          const defaultBrandDetails = {
            id: campaignData.brandId || 'unknown',
            name: 'Brand',
            logo: '/assets/default-logo.png',
            description: 'No description available.',
            website: '#',
            location: 'Not specified'
          };
          setBrandData(defaultBrandDetails);
          campaignData.brand = defaultBrandDetails;
        }
        
        setCampaign(campaignData);
        
        // Check if campaign requires draft approval (default to true for new pre-approval workflow)
        setRequiresDraftApproval(campaignData.requireDraftApproval !== false);
        
        // Check if the user has saved this campaign
        const isSaved = await savedCampaignService.isCampaignSaved(currentUser.uid, id);
        setSaved(isSaved);
        
        // Check if the user has already applied to this campaign - ALWAYS FETCH FRESH DATA
        const participantData = await campaignParticipantService.getParticipantByCampaignAndInfluencer(id, currentUser.uid);
        
        if (participantData) {
          setParticipantData(participantData);
          setApplicationStatus(participantData.status);
          
          // Always set draft status from fresh data to prevent stale status
          setDraftStatus(participantData.draftStatus || 'not_submitted');
          setDraftFeedback(participantData.draftFeedback || '');
          
          // Check if revision is requested
          if (participantData.submissionStatus === 'revision_requested') {
            setRevisionRequested(true);
            setRevisionFeedback(participantData.revisionFeedback || '');
          }
          
          // Check for review submission status
          if (participantData.reviewId) {
            try {
              const reviewRef = doc(db, 'reviews', participantData.reviewId);
              const reviewSnap = await getDoc(reviewRef);
              
              if (reviewSnap.exists()) {
                const reviewData = reviewSnap.data();
                setReviewSubmissionStatus(reviewData.status || 'pending');
                setReviewFeedback(reviewData.brandFeedback || reviewData.feedback || '');
              }
            } catch (error) {
              console.error('Error fetching review submission:', error);
            }
          } else if (participantData.submissionStatus === 'submitted') {
            // If there's a submission status but no reviewId yet
            setReviewSubmissionStatus('pending');
          }
          
          // Check for proof submission status
          if (participantData.proofId) {
            try {
              const proofRef = doc(db, 'proofs', participantData.proofId);
              const proofSnap = await getDoc(proofRef);
              
              if (proofSnap.exists()) {
                const proofData = proofSnap.data();
                setProofSubmissionStatus(proofData.status || 'pending');
                setProofFeedback(proofData.brandFeedback || proofData.feedback || '');
              }
            } catch (error) {
              console.error('Error fetching proof submission:', error);
            }
          }

          // Fetch tracking link if participant is approved
          if (participantData.status === 'approved') {
            try {
              setTrackingLinkLoading(true);
              const trackingLinkData = await trackingLinkService.getTrackingLink(id, currentUser.uid);
              
              if (trackingLinkData) {
                setTrackingLink(trackingLinkData);
                

                
                // Start content monitoring
                contentMonitoringService.monitorInfluencerContent(
                  id,
                  currentUser.uid,
                  trackingLinkData.trackingCode
                );
              } else {
                // If no tracking link exists, generate one now (fallback for missing links)
                try {
                  const generatedTrackingLink = await trackingLinkService.generateTrackingLink(
                    id,
                    currentUser.uid,
                    participantData.id
                  );
                  
                  if (generatedTrackingLink) {
                    setTrackingLink(generatedTrackingLink);
                    

                    
                    // Start content monitoring
                    contentMonitoringService.monitorInfluencerContent(
                      id,
                      currentUser.uid,
                      generatedTrackingLink.trackingCode
                    );
                  }
                } catch (generateError) {
                  console.error('Error generating tracking link as fallback:', generateError);
                }
              }
            } catch (error) {
              console.error('Error fetching tracking link:', error);
            } finally {
              setTrackingLinkLoading(false);
            }
          }
        }
        
        // Check if profile is complete
        if (userProfile) {
          const profileCheck = userService.isProfileComplete(userProfile);
          setIsProfileComplete(profileCheck.isComplete);
          setMissingFields(profileCheck.missingFields);
        }
        
        // Track campaign view for analytics
        analyticsService.trackCampaignView(id, currentUser.uid);
      } catch (err) {
        console.error("Error fetching campaign details:", err);
        setError('Failed to load campaign details. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchCampaignDetails();
    }
  }, [currentUser, id]);



  const toggleSaveCampaign = async () => {
    if (!currentUser || saving) return;

    const wasSaved = saved;

    // Optimistic update
    setSaved(!wasSaved);
    setSaving(true);

    try {
      await savedCampaignService.toggleSavedCampaign(currentUser.uid, id, wasSaved);

      analyticsService.trackUserEvent(currentUser.uid, wasSaved ? 'unsave_campaign' : 'save_campaign', {
        campaignId: id,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      // Rollback
      setSaved(wasSaved);
      showError(
        'Could not update saved campaign',
        'Something went wrong. Please try again.',
        3000
      );
    } finally {
      setSaving(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      showSuccess('Link copied!', 'Campaign link has been copied to your clipboard.', 2000);
    }).catch(() => {
      showError('Could not copy link', 'Please copy the URL from your browser manually.', 3000);
    });
  };

  const handleApply = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !userProfile) return;
    
    // Get CSRF token from form
    const form = new FormData(e.target);
    const csrfToken = form.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      return;
    }
    
    // Check if profile is complete before allowing application
    const profileCheck = userService.isProfileComplete(userProfile);
    if (!profileCheck.isComplete) {
      showError('Please complete your profile before applying to campaigns.', 'Add your profile information and photos to apply for campaigns.', 6000);
      
      // Update state with missing fields
      setIsProfileComplete(false);
      setMissingFields(profileCheck.missingFields);
      
      setApplying(false);
      return;
    }
    
    // Profile is complete, check if terms are accepted
    setIsProfileComplete(true);
    setMissingFields([]);
    
    // Prepare application data
    const applicationData = {
      note: sanitizeText(applicationNote),
      influencerName: sanitizeText(userProfile.displayName || currentUser.displayName),
      influencerProfileUrl: sanitizeText(userProfile.profileImageUrl),
      platforms: userProfile.socialMediaPlatforms ? userProfile.socialMediaPlatforms.map(p => sanitizeText(p)) : []
    };
    
    // Show Terms and Conditions modal if not accepted yet
    if (!termsAcceptedDate) {
      setPendingApplicationData(applicationData);
      setShowTermsModal(true);
      return;
    }
    
    // Proceed with application submission
    await submitApplication(applicationData);
  };

  const submitApplication = async (applicationData) => {
    setApplying(true);
    
    try {
      // Add terms acceptance data to application
      const finalApplicationData = {
        ...applicationData,
        termsAcceptedDate: termsAcceptedDate
      };
      
      // Create application using campaignParticipantService
      await campaignParticipantService.applyToCampaign(id, currentUser.uid, finalApplicationData);
      
      setApplicationStatus('pending');
      setShowApplyForm(false);
      
      // Show success feedback
      showSuccess('Application submitted successfully!', 'Your campaign application has been submitted and is pending brand approval.', 4000);
      
      // Track application analytics
      analyticsService.trackUserEvent(currentUser.uid, 'apply_to_campaign', {
        campaignId: id,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error("Error applying to campaign:", error);
      
      // Show error feedback
      showError('Failed to submit application.', 'Please check your connection and try again.', 6000);
    } finally {
      setApplying(false);
    }
  };

  const handleTermsAccept = async (effectiveDate) => {
    setTermsAcceptedDate(effectiveDate);
    setShowTermsModal(false);
    
    // Submit application with the pending data
    if (pendingApplicationData) {
      await submitApplication(pendingApplicationData);
      setPendingApplicationData(null);
    }
  };

  const handleTermsClose = () => {
    setShowTermsModal(false);
    setPendingApplicationData(null);
    setApplying(false);
  };

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="flex justify-center mb-3">
          <AlertTriangle className="h-10 w-10 text-red-400" />
        </div>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Try Again
        </button>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-1">Campaign not found</h3>
        <p className="text-gray-500 mb-4">
          The campaign you're looking for doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate('/influencer/campaigns')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
        >
          Back to Campaigns
        </button>
      </div>
    );
  }




  
  return (
    <div className="relative">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Campaigns', to: '/influencer/campaigns' },
          { label: campaign?.name || 'Campaign' },
        ]}
      />

      {/* Revision Request Alert */}
      {revisionRequested && (
        <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-orange-400" aria-hidden="true" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-orange-800">Revision Requested</h3>
              <div className="mt-2 text-sm text-orange-700">
                <p>The brand has requested revisions to your proof submission. Please review the feedback below and resubmit your proof.</p>
                {revisionFeedback && (
                  <div className="mt-3 p-3 bg-white rounded border border-orange-200">
                    <p className="text-sm font-medium text-gray-900 mb-1">Brand Feedback:</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{revisionFeedback}</p>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate(`/influencer/proof/submit/${id}`)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 transition-colors"
                >
                  Resubmit Proof
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Submission Status Banner */}
      {applicationStatus === 'approved' && reviewSubmissionStatus && (
        <div className={`mb-6 p-4 rounded-lg ${reviewSubmissionStatus === 'approved' ? 'bg-green-50 border border-green-200' : reviewSubmissionStatus === 'pending' ? 'bg-yellow-50 border border-yellow-200' : reviewSubmissionStatus === 'rejected' ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="flex items-center">
            {reviewSubmissionStatus === 'approved' ? (
              <>
                <BadgeCheck className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-sm font-medium text-green-700">Final review approved by brand. Payment will be processed once the campaign ends.</span>
              </>
            ) : reviewSubmissionStatus === 'pending' ? (
              <>
                <Clock className="h-5 w-5 text-yellow-500 mr-2" />
                <span className="text-sm font-medium text-yellow-700">Final review pending approval from brand.</span>
              </>
            ) : reviewSubmissionStatus === 'rejected' ? (
              <>
                <X className="h-5 w-5 text-orange-500 mr-2" />
                <div>
                  <span className="text-sm font-medium text-orange-700">Final review needs revision. Please resubmit your review with the requested changes.</span>
                  {reviewFeedback && <span className="text-xs mt-1 text-red-600 block">Feedback from brand: {reviewFeedback}</span>}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* 2-COLUMN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ===== LEFT COLUMN (main content) ===== */}
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign Header Card */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between">
                <div className="flex items-start">
                  <div className="h-16 w-16 rounded-full mr-4 overflow-hidden bg-gray-100 flex-shrink-0">
                    {campaign.brand?.logo && campaign.brand.logo !== '/assets/default-logo.png' ? (
                      <img src={campaign.brand.logo} alt={campaign.brand?.name || 'Brand'} className="h-full w-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = '/assets/default-logo.png'; }} />
                    ) : (
                      <img src="/assets/default-logo.png" alt={campaign.brand?.name || 'Brand'} className="h-full w-full object-cover" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">{campaign?.name || campaign?.title || 'Campaign'}</h2>
                    <div className="flex items-center mt-1">
                      <p className="text-sm text-gray-600">by</p>
                      <p className="ml-1 text-sm font-medium text-primary">{brandData?.name || 'Brand'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handleShare} className="text-gray-400 hover:text-primary p-2 rounded-md hover:bg-gray-50 transition-colors" aria-label="Share campaign" title="Share campaign">
                    <Share2 className="h-5 w-5" />
                  </button>
                  <button onClick={toggleSaveCampaign} disabled={saving} className="text-gray-400 hover:text-primary p-2 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" aria-label={saved ? 'Remove from saved' : 'Save campaign'} title={saved ? 'Remove from saved' : 'Save campaign'}>
                    {saving ? <RefreshCw className="h-5 w-5 animate-spin" /> : saved ? <Bookmark className="h-5 w-5 text-primary" fill="currentColor" /> : <Bookmark className="h-5 w-5" />}
                  </button>
                  <ReportFlag
                    targetType="campaign"
                    targetId={id}
                    targetName={campaign?.name || campaign?.title || 'Campaign'}
                    buttonStyle="icon"
                  />
                </div>
              </div>

              {/* Badges */}
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <Tag className="h-3 w-3 mr-1" />
                  {campaign.category}
                </span>
                {isCampaignActive(campaign) && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                )}
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <Users className="h-3 w-3 mr-1" />
                  {Math.max(0, (campaign.totalSpots || 0) - (campaign.filledSpots || 0))} of {campaign.totalSpots || 0} spots left
                </span>
              </div>

              {/* Urgency indicators */}
              {(() => {
                const daysLeft = getDaysUntil(campaign.applicationDeadline || campaign.endDate);
                const spotsLeft = Math.max(0, (campaign.totalSpots || 0) - (campaign.filledSpots || 0));
                return (
                  <>
                    {daysLeft !== null && daysLeft <= 3 && daysLeft >= 0 && !applicationStatus && (
                      <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-orange-600 bg-orange-50 px-3 py-2 rounded-md">
                        <Clock className="h-4 w-4" />
                        {daysLeft === 0 ? 'Deadline is today!' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left to apply`}
                      </div>
                    )}
                    {spotsLeft > 0 && spotsLeft <= 3 && (
                      <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-red-600 bg-red-50 px-3 py-2 rounded-md">
                        <Users className="h-4 w-4" />
                        Only {spotsLeft} spot{spotsLeft === 1 ? '' : 's'} left!
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Campaign Image */}
              {campaign.imageUrl && (
                <div className="mt-6">
                  <img src={campaign.imageUrl} alt={campaign.name || campaign.title || 'Campaign'} className="w-full h-64 object-cover rounded-lg" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
          </div>

          {/* Campaign Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Campaign Description</h3>
            <div className="text-sm text-gray-600">
              {(() => {
                const description = campaign.longDescription || campaign.description || '';
                const formatDescription = (text) => {
                  if (!text) return <p>No description available.</p>;
                  const paragraphs = text.split('\n\n');
                  return paragraphs.map((paragraph, pIndex) => {
                    const lines = paragraph.split('\n').filter(line => line.trim());
                    const hasNumberedItems = lines.some(line => /^\d+\./.test(line.trim()));
                    const hasBulletItems = lines.some(line => /^[•\-\*]/.test(line.trim()));
                    if (hasNumberedItems) {
                      return (
                        <ol key={pIndex} className="mb-4 space-y-2 list-decimal list-inside">
                          {lines.map((line, lIndex) => {
                            const trimmedLine = line.trim();
                            const numberedMatch = trimmedLine.match(/^(\d+)\.(.*)/);
                            if (numberedMatch) return <li key={lIndex} className="leading-relaxed">{numberedMatch[2].trim()}</li>;
                            if (trimmedLine) return <div key={lIndex} className="ml-6 text-gray-600 leading-relaxed">{trimmedLine}</div>;
                            return null;
                          }).filter(Boolean)}
                        </ol>
                      );
                    } else if (hasBulletItems) {
                      return (
                        <ul key={pIndex} className="mb-4 space-y-2 list-disc list-inside">
                          {lines.map((line, lIndex) => {
                            const trimmedLine = line.trim();
                            const cleanLine = trimmedLine.replace(/^[•\-\*]\s*/, '');
                            if (cleanLine) return <li key={lIndex} className="leading-relaxed">{cleanLine}</li>;
                            return null;
                          }).filter(Boolean)}
                        </ul>
                      );
                    } else {
                      return <p key={pIndex} className="mb-4 leading-relaxed">{paragraph}</p>;
                    }
                  });
                };
                return formatDescription(description);
              })()}
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Requirements</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Content Requirements</h4>
                <div className="text-sm text-gray-600 whitespace-pre-wrap">
                  {campaign.contentGuidelines || campaign.requirements || 'No specific requirements provided.'}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Preferred Platforms</h4>
                <div className="flex flex-wrap gap-2">
                  {(campaign.requiredPlatforms || campaign.preferredPlatforms || []).map((platform, index) => (
                    <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{platform}</span>
                  ))}
                  {(campaign.requiredPlatforms || campaign.preferredPlatforms || []).length === 0 && (
                    <span className="text-sm text-gray-600">No specific platforms required</span>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Minimum Followers</h4>
                <p className="text-sm text-gray-600">{(campaign.minFollowers || campaign.requiredFollowers || 0).toLocaleString()}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Requirements</h4>
                <ul className="text-sm text-gray-600 list-disc pl-5">
                  {campaign.additionalInfo && <li>{campaign.additionalInfo}</li>}
                  {campaign.requiredHashtags && <li>Hashtags: {campaign.requiredHashtags}</li>}
                  {campaign.requiredMentions && <li>Mentions: {campaign.requiredMentions}</li>}
                  {(campaign.additionalRequirements || []).map((req, index) => <li key={index}>{req}</li>)}
                  {!campaign.additionalInfo && !campaign.requiredHashtags && !campaign.requiredMentions && (campaign.additionalRequirements || []).length === 0 && <li>No additional requirements specified</li>}
                </ul>
              </div>
            </div>
          </div>

          {/* Store Coverage */}
          {campaign.storeSelection && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Store Coverage</h3>
              <div className="flex items-center mb-3">
                <Store className="h-5 w-5 text-gray-400 mr-2" />
                <h4 className="text-sm font-medium text-gray-700">Campaign applies to:</h4>
              </div>
              {campaign.storeSelection.type === 'all' && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-green-700">All store locations</span>
                  <p className="mt-1">This campaign applies to all of the brand's store locations.</p>
                </div>
              )}
              {campaign.storeSelection.type === 'specific' && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-blue-700">{campaign.storeSelection.selectedStores?.length || 0} specific store(s)</span>
                  {campaign.storeSelection.selectedStores?.length > 0 && <p className="mt-1">Campaign applies to selected store locations only.</p>}
                </div>
              )}
              {campaign.storeSelection.type === 'regional' && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-purple-700">Regional coverage</span>
                  {campaign.storeSelection.selectedRegions?.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium">Regions:</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {campaign.storeSelection.selectedRegions.map((region, index) => (
                          <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">{region}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Campaign Progress (if approved) */}
          {applicationStatus === 'approved' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Progress</h3>
              <CampaignProgressIndicator currentStage={getCurrentStage()} userType="influencer" />
              <div className="mt-4 text-center">{getCurrentStatusMessage()}</div>
            </div>
          )}
        </div>

        {/* ===== RIGHT COLUMN (sidebar) ===== */}
        <div className="lg:col-span-1 space-y-6 lg:self-start lg:sticky lg:top-6 z-20">
          {/* Key Info Card */}
          <div className="bg-white rounded-lg shadow p-6 lg:sticky lg:top-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Key Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center text-gray-500">
                  <Banknote className="h-4 w-4 mr-2" />
                  Compensation
                </div>
                <span className="font-semibold text-gray-900">{getCompensationLabel(campaign)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center text-gray-500">
                  <Calendar className="h-4 w-4 mr-2" />
                  Start Date
                </div>
                <span className="font-medium text-gray-900">{formatDate(campaign.startDate)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center text-gray-500">
                  <Calendar className="h-4 w-4 mr-2" />
                  Apply By
                </div>
                <span className="font-medium text-gray-900">{formatDate(campaign.applicationDeadline || campaign.endDate)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center text-gray-500">
                  <Clock className="h-4 w-4 mr-2" />
                  Review By
                </div>
                <span className="font-medium text-gray-900">{formatDate(campaign.reviewDeadline || campaign.deadline)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center text-gray-500">
                  <Calendar className="h-4 w-4 mr-2" />
                  End Date
                </div>
                <span className="font-medium text-gray-900">{formatDate(campaign.endDate)}</span>
              </div>
              <div className="flex justify-between items-center text-sm pt-3 border-t border-gray-100">
                <div className="flex items-center text-gray-500">
                  <Users className="h-4 w-4 mr-2" />
                  Spots Left
                </div>
                <span className="font-semibold text-gray-900">{Math.max(0, (campaign.totalSpots || 0) - (campaign.filledSpots || 0))} / {campaign.totalSpots || 0}</span>
              </div>
            </div>
          </div>

          {/* Status & Actions Card */}
          <div className="bg-white rounded-lg shadow p-6">
            {applicationStatus ? (
              <div className="space-y-4">
                {/* Status Display - Priority Order */}
                {(() => {
                  if ((draftStatus === 'rejected' || draftStatus === 'revision_requested') && draftFeedback) {
                    return (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                          <div className="flex-1">
                            <h3 className="text-sm font-semibold text-orange-800 mb-2">Draft Revision Requested</h3>
                            <p className="text-xs text-orange-700 mb-3 whitespace-pre-wrap">{draftFeedback}</p>
                            <button onClick={() => { showSuccess('Navigating to draft resubmission...', 'Redirecting you to resubmit your draft.', 2000); navigate(`/influencer/draft/submit/${id}`); }} className="inline-flex items-center px-3 py-1.5 bg-orange-600 text-white text-xs font-medium rounded-md hover:bg-orange-700 transition-colors">Resubmit Draft</button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  if ((proofSubmissionStatus === 'rejected' || reviewSubmissionStatus === 'rejected') && applicationStatus === 'approved') {
                    return (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <X className="h-5 w-5 text-orange-500 mr-3" />
                            <div>
                              <h3 className="text-sm font-semibold text-orange-800">Content Revision Needed</h3>
                              <p className="text-xs text-orange-600">Your submitted content needs revision.</p>
                            </div>
                          </div>
                          <button onClick={() => { showSuccess('Navigating to proof resubmission...', 'Redirecting you to update your proof submission.', 2000); navigate(`/influencer/proof/submit/${id}`); }} className="inline-flex items-center px-3 py-1.5 bg-orange-600 text-white text-xs font-medium rounded-md hover:bg-orange-700 transition-colors">Resubmit Proof</button>
                        </div>
                      </div>
                    );
                  }
                  if ((proofSubmissionStatus === 'approved' || reviewSubmissionStatus === 'approved') && applicationStatus === 'approved') {
                    return (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <BadgeCheck className="h-5 w-5 text-green-500 mr-3" />
                          <div>
                            <h3 className="text-sm font-semibold text-green-800">Content Approved!</h3>
                            <p className="text-xs text-green-600">Payment will be processed after the campaign ends.</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  if ((proofSubmissionStatus === 'pending' || reviewSubmissionStatus === 'pending') && applicationStatus === 'approved') {
                    return (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <Clock className="h-5 w-5 text-yellow-500 mr-3" />
                          <div>
                            <h3 className="text-sm font-semibold text-yellow-800">Content Under Review</h3>
                            <p className="text-xs text-yellow-600">Your submitted content is being reviewed by the brand.</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  if ((draftStatus === 'pending' || draftStatus === 'submitted') && applicationStatus === 'approved') {
                    return (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <Clock className="h-5 w-5 text-blue-500 mr-3" />
                          <div>
                            <h3 className="text-sm font-semibold text-blue-800">Draft Under Review</h3>
                            <p className="text-xs text-blue-600">Your content draft is being reviewed by the brand.</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  if (applicationStatus === 'approved') {
                    if (requiresDraftApproval) {
                      if (draftStatus === 'approved') {
                        return (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <BadgeCheck className="h-5 w-5 text-blue-500 mr-3" />
                                <div>
                                  <h3 className="text-sm font-semibold text-blue-800">Draft Approved</h3>
                                  <p className="text-xs text-blue-600">Post your content and submit proof.</p>
                                </div>
                              </div>
                              <button onClick={() => { showSuccess('Navigating to proof submission...', 'Redirecting you to submit your proof.', 2000); navigate(`/influencer/proof/submit/${id}`); }} className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors">Submit Proof</button>
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <BadgeCheck className="h-5 w-5 text-green-500 mr-3" />
                                <div>
                                  <h3 className="text-sm font-semibold text-green-800">Application Approved</h3>
                                  <p className="text-xs text-green-600">Submit your content draft for brand approval.</p>
                                </div>
                              </div>
                              <button onClick={() => { showSuccess('Navigating to draft submission...', 'Redirecting you to submit your content draft.', 2000); navigate(`/influencer/draft/submit/${id}`); }} className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors">Submit Draft</button>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      return (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <BadgeCheck className="h-5 w-5 text-green-500 mr-3" />
                              <div>
                                <h3 className="text-sm font-semibold text-green-800">Application Approved</h3>
                                <p className="text-xs text-green-600">Post your content and submit proof.</p>
                              </div>
                            </div>
                            <button onClick={() => { showSuccess('Navigating to proof submission...', 'Redirecting you to submit your proof.', 2000); navigate(`/influencer/proof/submit/${id}`); }} className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors">Submit Proof</button>
                          </div>
                        </div>
                      );
                    }
                  }
                  if (applicationStatus === 'pending') {
                    return (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <Clock className="h-5 w-5 text-yellow-500 mr-3" />
                          <div>
                            <h3 className="text-sm font-semibold text-yellow-800">Application Under Review</h3>
                            <p className="text-xs text-yellow-600">Your application is pending brand review.</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  if (applicationStatus === 'rejected') {
                    return (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <X className="h-5 w-5 text-red-500 mr-3" />
                          <div>
                            <h3 className="text-sm font-semibold text-red-800">Application Rejected</h3>
                            <p className="text-xs text-red-600">Please check feedback and reapply if possible.</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Tracking Link */}
                {trackingLinkLoading ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Loading tracking link...</span>
                  </div>
                ) : trackingLink ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-blue-800 mb-2">Your Tracking Link</h4>
                    <div className="flex items-center bg-white border border-blue-200 rounded-md p-2">
                      <input type="text" value={trackingLink.trackingUrl} readOnly className="flex-1 text-xs text-gray-700 bg-transparent border-none focus:outline-none" />
                      <button onClick={() => { navigator.clipboard.writeText(trackingLink.trackingUrl); showSuccess('Tracking URL copied!', 'The tracking link has been copied to your clipboard.', 3000); }} className="ml-2 px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors">Copy</button>
                    </div>
                  </div>
                ) : null}

                {/* Review Brand — shown when campaign is completed and influencer was approved */}
                {applicationStatus === 'approved' && campaign?.status === 'completed' && participantData && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start">
                        <Star className="h-5 w-5 text-amber-500 mr-3 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-semibold text-amber-800">Rate Your Experience</h3>
                          <p className="text-xs text-amber-600 mt-0.5">Share your feedback about working with {campaign?.brand?.name || 'this brand'}.</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowReviewModal(true)}
                        className="inline-flex items-center px-3 py-1.5 border border-amber-300 text-xs font-medium rounded-md text-amber-700 bg-white hover:bg-amber-100 transition-colors"
                      >
                        <Star className="h-3 w-3 mr-1" />
                        Review Brand
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : showApplyForm ? (
              <form onSubmit={handleApply} className="space-y-4">
                <CSRFToken />
                <div>
                  <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
                    Why are you a good fit? (Optional)
                  </label>
                  <textarea id="note" rows="3" value={applicationNote} onChange={(e) => setApplicationNote(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary focus:border-primary" placeholder="Share your experience with similar products..."></textarea>
                </div>
                <div className="flex items-center justify-end space-x-3">
                  <button type="button" onClick={() => setShowApplyForm(false)} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">Cancel</button>
                  <button type="submit" disabled={applying} className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors">
                    {applying ? <><LoadingSpinner size="xs" color="white" /> Applying...</> : 'Submit Application'}
                  </button>
                </div>
              </form>
            ) : isCampaignActive(campaign) ? (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-base font-medium text-gray-900 mb-1">Ready to Join?</p>
                  <p className="text-sm text-gray-600">Apply now to be considered for this campaign.</p>
                </div>
                {!isProfileComplete && (
                  <div className="p-3 border border-yellow-300 bg-yellow-50 rounded-lg">
                    <div className="flex items-start">
                      <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="text-xs font-medium text-yellow-800">Profile Incomplete</h3>
                        <p className="text-xs text-yellow-700 mt-1 mb-2">Complete your profile before applying.</p>
                        <div className="space-y-1 mb-3">
                          {missingFields.map((field, index) => (
                            <div key={index} className="flex items-center">
                              <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full mr-2"></div>
                              <span className="text-xs text-yellow-800 font-medium">{field === 'all' ? 'All profile information' : field}</span>
                            </div>
                          ))}
                        </div>
                        <button onClick={() => navigate('/influencer/profile')} className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 transition-colors">Complete Profile</button>
                      </div>
                    </div>
                  </div>
                )}
                <button onClick={() => setShowApplyForm(true)} className="w-full bg-primary text-white py-3 px-6 rounded-lg hover:bg-primary-dark transition duration-150 ease-in-out flex items-center justify-center text-base font-medium shadow-sm" disabled={!isProfileComplete}>
                  <Send className="h-5 w-5 mr-2" />
                  Apply for Campaign
                </button>
              </div>
            ) : (
              <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-base font-medium text-gray-700 mb-1">Campaign Ended</p>
                <p className="text-sm text-gray-600">This campaign is no longer accepting applications.</p>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Timeline</h3>
            <div className="relative">
              <div className="absolute left-4 top-0 h-full w-0.5 bg-gray-200"></div>
              <ul className="space-y-5">
                {[
                  { stage: 'Campaign Start', date: campaign.startDate },
                  { stage: 'Application Deadline', date: campaign.applicationDeadline || campaign.endDate },
                  { stage: 'Review Deadline', date: campaign.reviewDeadline || campaign.deadline },
                  { stage: 'Campaign End', date: campaign.endDate }
                ].map((item, index) => {
                  const daysUntil = getDaysUntil(item.date);
                  const isPast = daysUntil !== null && daysUntil < 0;
                  const isFuture = daysUntil !== null && daysUntil >= 0;
                  return (
                    <li key={index} className="relative pl-10">
                      <div className={`absolute left-0 top-0.5 h-8 w-8 rounded-full flex items-center justify-center text-white text-xs ${isPast ? 'bg-green-500' : isFuture ? 'bg-primary' : 'bg-gray-300'}`}>
                        {isPast ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{item.stage}</h4>
                        <p className="text-xs text-gray-500">{formatDate(item.date)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* About the Brand */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">About the Brand</h3>
            <h4 className="text-sm font-medium text-gray-900">{campaign.brand?.name || 'Brand'}</h4>
            <p className="text-sm text-gray-500 mt-1 mb-3">{campaign.brand?.description || 'No description available.'}</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Website</span>
                {(campaign.brand?.website && campaign.brand.website !== '#') || campaign.website ? (
                  <a href={campaign.brand?.website || campaign.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{(campaign.brand?.website || campaign.website).replace(/^https?:\/\//, '')}</a>
                ) : (
                  <span className="text-gray-600">Not available</span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Category</span>
                <span className="text-gray-900 font-medium">{campaign.category || 'Not specified'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Campaigns</span>
                <span className="text-gray-900 font-medium">{activeCampaignCount} active</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* More from this Brand */}
      {otherCampaigns.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">More from {brandData?.name || 'this Brand'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherCampaigns.map((c) => {
              const spotsLeft = Math.max(0, (c.totalSpots || 0) - (c.filledSpots || 0));
              return (
                <Link key={c.id} to={`/influencer/campaigns/${c.id}`} className="bg-white rounded-lg shadow-md border border-gray-100 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col">
                  <div className="w-full h-28 overflow-hidden rounded-t-lg relative bg-gradient-to-br from-primary/10 to-blue-400/10">
                    {c.imageUrl ? <img src={c.imageUrl} alt={c.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Sparkles className="h-8 w-8 text-primary/30" /></div>}
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <h4 className="text-sm font-semibold text-gray-900 line-clamp-1 mb-1">{c.name}</h4>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{c.category}</span>
                    </div>
                    <div className="mt-auto pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
                      <span className="font-semibold text-gray-900">{getCompensationLabel(c)}</span>
                      <span className="text-gray-500">{spotsLeft} spots left</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={handleTermsClose}
        onAccept={handleTermsAccept}
        title="Campaign Application Terms and Conditions"
        acceptButtonText="Accept and Apply to Campaign"
      />

      {/* Review Brand Modal */}
      {showReviewModal && participantData && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          campaignId={id}
          participantId={participantData.id}
          revieweeId={campaign?.brandId}
          revieweeType="brand"
          revieweeName={campaign?.brand?.name || 'Brand'}
          reviewerType="influencer"
        />
      )}
    </div>
  );
};

export default CampaignDetails;
